/** Chat-side analytics events (AGENTS.md 9.4): ids, types and timings only. */
import type { WidgetEvent } from "@wapadesk/schema";
import type { ApiClient } from "./api";

export interface TrackContext {
  clinicId: string;
  visitId?: string;
  pagePath?: string;
  device?: "mobile" | "tablet" | "desktop";
  locale?: string;
  formId?: string;
  formVersionId?: string;
  utm?: Record<string, string>;
}

export class Tracker {
  private queue: WidgetEvent[] = [];
  private timer: ReturnType<typeof setTimeout> | undefined;
  ctx: TrackContext;
  constructor(
    private readonly api: Pick<ApiClient, "events">,
    ctx: TrackContext,
    private readonly delayMs = 1200,
  ) {
    this.ctx = ctx;
    if (typeof window !== "undefined") window.addEventListener("pagehide", () => this.flush());
  }
  track(type: WidgetEvent["type"], data?: WidgetEvent["data"]): void {
    const c = this.ctx;
    this.queue.push({
      type,
      at: new Date().toISOString(),
      clinicId: c.clinicId,
      ...(c.visitId ? { visitId: c.visitId } : {}),
      ...(c.pagePath ? { pagePath: c.pagePath } : {}),
      ...(c.device ? { device: c.device } : {}),
      ...(c.locale ? { locale: c.locale } : {}),
      ...(c.formId ? { formId: c.formId } : {}),
      ...(c.formVersionId ? { formVersionId: c.formVersionId } : {}),
      ...(c.utm ? { utm: c.utm } : {}),
      ...(data ? { data } : {}),
    });
    if (this.queue.length >= 20) this.flush();
    else if (!this.timer) this.timer = setTimeout(() => this.flush(), this.delayMs);
  }
  flush(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = undefined;
    if (!this.queue.length) return;
    const batch = this.queue.splice(0, 50);
    void this.api.events(batch).catch(() => undefined);
  }
}
